/**
 * A module — the playlist rules.
 *
 * A **module** is a post whose body is an ordered list of capsules from one
 * subject at one level: a teacher gathers six of their own capsules into a course
 * and a learner plays them as one run. This file is the part of that with no
 * screen, no network and no React in it — what may go in a playlist, what state
 * each place in it is in, and what stops an unfinished one from being published —
 * because those are the questions whose answers have to be the *same* in the
 * studio, in the picker and in a classroom, and the only way to hold three
 * callers to one answer is to have one answer written down.
 *
 * Three facts about a capsule are load-bearing, and all three are already on the
 * record the pickers read (`#/state/queries/capsule`):
 *
 *  • **`level` and `subject`** — the codes the publisher chose (`Level_A5`,
 *    `Maths`). A module *is* one subject at one level, so this pair is what a
 *    candidate is filtered by, and what a stray item is refused by.
 *  • **`status` and `post`** — a capsule with no post is a draft, and a playlist
 *    of drafts is a playlist nobody can play. It may be *put* in a module (that is
 *    how writing one inside a module works) but the module cannot be published
 *    until it is published.
 *  • **`slideCount`** — the exercises in the capsule, as the publisher counted
 *    them. Summed over the playlist, it is the module's own "coins to win".
 *
 * What is deliberately *not* here: which of an author's records are theirs to
 * share. That is a privacy question with its own answer in `#/lib/profileWork`,
 * and this file reads the rule it is handed rather than restating it.
 */
import {type ActorCapsuleRecord} from '#/state/queries/capsule'

/** The collection a module record lives in, beside the work it gathers. */
export const MODULE_COLLECTION = 'app.bsky.feed.module'

/** The marker a module's post carries, the way `type: 'capsule'` marks one. */
export const MODULE_POST_TYPE = 'module'

/**
 * How many capsules a module needs before it may be published.
 *
 * Six is the floor the feature was asked for, and it is a *gate* rather than a
 * suggestion: a playlist of two capsules is not a course, and a leaderboard over
 * two capsules measures nothing. It is a constant in one place because the
 * studio, the room's grid and the profile's tab all have to agree on it — and
 * because the day it changes it must change in one line, not in four sentences
 * of copy that nobody re-reads.
 */
export const MODULE_MIN_CAPSULES = 6

/**
 * How many learners must finish a module before its top three are shown.
 *
 * Below this the module shows its **results** — every play, with its score and
 * its time — and above it the same ranking is cut to a podium. The number is a
 * threshold on *finishers*, not on plays: a hundred half-runs are not a
 * competition, and the whole point of the gate is that the board appears when
 * enough people have actually gone the whole way round.
 */
export const MODULE_LEADERBOARD_MIN_FINISHERS = 100

/**
 * How many players a module needs before its card names the three in the lead.
 *
 * A *second*, lower gate, and it counts something the first one does not: the
 * flood of learners who have **played**, finishers or not. Ninety is the point at
 * which the class is plainly a crowd rather than a room — too many names to know
 * and no reason to hide who is out in front — so past it the card stops saying
 * *seventeen players so far* and starts saying who those players are. Below it,
 * naming three of nine is a small room with an empty trophy shelf, and the count
 * is the more honest sentence.
 *
 * It is deliberately under `MODULE_LEADERBOARD_MIN_FINISHERS` rather than equal to
 * it: the faces are worth showing while the podium is still locked, and the two
 * gates answer different questions (*who is ahead* and *has anybody finished*).
 */
export const MODULE_LEADERBOARD_LEADERS_PLAYS = 90

/**
 * One place in a playlist: a capsule record, in order.
 *
 * **Two addresses, for the reason a classroom's entry keeps two.** The capsule
 * record says what the work *is* — its title, its level, its subject, how many
 * exercises are in it — and the post it was published as is what everything that
 * *draws* the work reads: a feed card, a thumbnail, a moderation decision. A
 * module's carousel is a row of those cards, so it needs the posts; the gate and
 * the coin total need the record.
 *
 * `postUri` is absent for a capsule written inside the module that has not been
 * published yet, which is exactly the state the gate refuses — so the empty value
 * carries the same meaning here as it does in `moduleSlots`, and neither has to
 * ask a second source what a draft is.
 */
export type ModuleItem = {
  /** AT-URI of the `app.bsky.feed.capsule` record. */
  uri: string
  /** Its cid when the caller has one; the item is written without it otherwise. */
  cid?: string
  /** The post the capsule was published as. Absent for a draft. */
  postUri?: string
}

/**
 * One of an author's capsules, as the rules need it.
 *
 * A structural subset of `ActorCapsuleRecord`, which satisfies it as it stands —
 * so a caller hands over the records it already fetched and nothing has to be
 * mapped between two shapes that would then be free to disagree.
 */
export type ModuleCapsuleFacts = {
  uri: string
  title?: string
  /** The grade code the publisher chose (`Level_A5`), not a name. */
  level?: string
  subject?: string
  /** `draft` while the capsule is unfinished; absent on records written before. */
  status?: string
  /** The post the capsule was published as. Absent for a draft. */
  postUri?: string
  /** The capsule's *exercises*, as the publisher counted them. */
  slideCount?: number
  /** `classroom` when the capsule was written for one room. */
  visibility?: string
}

/**
 * Whether a capsule is filed under the module's own subject and level.
 *
 * Both must be named by both sides. A capsule with no level is not "close
 * enough": the module *is* its pair of codes — that is the promise its card makes
 * to a learner — and a playlist that quietly mixed a 7th-year capsule into a 5th
 * year course would be the one way a module could lie about what it holds.
 */
export function isSameSubjectAndLevel(
  capsule: Pick<ModuleCapsuleFacts, 'level' | 'subject'>,
  module: {
    level: string | null | undefined
    subject: string | null | undefined
  },
): boolean {
  if (!module.level || !module.subject) return false
  return capsule.level === module.level && capsule.subject === module.subject
}

/**
 * The capsules a module at this subject and level may be built from.
 *
 * Four exclusions, in the order they matter:
 *
 *  • **A different level or subject** is never offered. The promise of a module
 *    is that its capsules belong together, and the picker is where that promise
 *    is kept — a card that was visible and then refused would be a rule the
 *    creator has to learn by being told off.
 *  • **A capsule already in the playlist** is left out (the caller names them in
 *    `taken`), so the picker can only ever produce a playlist without repeats.
 *  • **Another room's own capsule** is left out. A capsule written for a
 *    classroom says `visibility: 'classroom'` on its record, which names no room
 *    — so the app cannot prove one belongs to the room being built in, and the
 *    safe reading is the honest one: a room's own work is not offered as material
 *    for a module that is about to be shared.
 *  • **Drafts are offered, and badged.** Writing a capsule *inside* a module is
 *    the flow this feature exists for, and it starts with a draft — so keeping
 *    drafts out of the picker would send a creator looking for work they can see
 *    in their own list. They are ordered after the published ones and the module
 *    refuses to publish until they are published, which is the rule stated where
 *    it bites rather than where it merely irritates.
 */
export function moduleCandidates<T extends ModuleCapsuleFacts>(
  records: readonly T[] | undefined,
  {
    level,
    subject,
    taken,
  }: {
    level: string | null | undefined
    subject: string | null | undefined
    /** The uris already in the playlist. */
    taken: readonly string[]
  },
): T[] {
  const excluded = new Set(taken)
  return (records ?? [])
    .filter(record => isSameSubjectAndLevel(record, {level, subject}))
    .filter(record => record.visibility !== 'classroom')
    .filter(record => !excluded.has(record.uri))
    .sort((a, b) => {
      // Published first, then drafts: the work that can be played now is what a
      // creator is looking for, and a draft is a decision they have already made.
      const aDraft = a.status === 'draft' || !a.postUri
      const bDraft = b.status === 'draft' || !b.postUri
      if (aDraft !== bDraft) return aDraft ? 1 : -1
      return (a.title ?? '').localeCompare(b.title ?? '')
    })
}

/**
 * What the capsule picker sheet shows, from the library and the two lists it
 * knows about: what the playlist already holds (`taken`) and what the creator
 * has tapped in this sheet (`order`).
 *
 * Tapped capsules stay among the candidates, so their numbered badge shows and a
 * second tap undoes them. The sheet is only *finished* (`all-chosen`) when every
 * capsule at the pair is already in the playlist and nothing is pending here;
 * deciding that from the candidates alone hid the submit button on the last tap
 * whenever the library held exactly enough capsules.
 */
export function modulePickerState<T extends ModuleCapsuleFacts>(
  records: readonly T[] | undefined,
  {
    level,
    subject,
    taken,
    order,
    alreadyPlaced = taken.length,
  }: {
    level: string | null | undefined
    subject: string | null | undefined
    taken: readonly string[]
    order: readonly string[]
    /** Places already filled in the playlist; defaults to `taken.length`. */
    alreadyPlaced?: number
  },
): {
  candidates: T[]
  view: 'choose' | 'all-chosen' | 'nothing'
  /** How many more capsules the module needs after this selection. */
  missingAfter: number
} {
  const candidates = moduleCandidates(records, {level, subject, taken})
  const atThisPair = (records ?? []).filter(
    record =>
      isSameSubjectAndLevel(record, {level, subject}) &&
      record.visibility !== 'classroom',
  )
  const view =
    candidates.length > 0 || order.length > 0
      ? 'choose'
      : atThisPair.length > 0
        ? 'all-chosen'
        : 'nothing'
  return {
    candidates,
    view,
    missingAfter: Math.max(
      0,
      MODULE_MIN_CAPSULES - (alreadyPlaced + order.length),
    ),
  }
}

/** The subject and level an author works at, as a module's own two codes. */
export type ModuleFiling = {level: string; subject: string}

/**
 * Where an author's work mostly lives, as the answer to *what is this module*.
 *
 * Starting a module means answering *one subject, one level* before anything can
 * be chosen, because that pair is what filters the picker. But the answer is
 * almost never in doubt: a creator who has written forty capsules at 5th-year
 * maths is starting almost every module at 5th-year maths, and asking them to
 * say so three times (school, then year, then subject) is three taps that buy
 * nothing. So the picker's question is asked *once*, by the app, from the work
 * the author already has — and the studio pre-selects the answer.
 *
 * The rule is **the pair they have most of**, not the most recent one: a teacher
 * who tried one capsule at another level last week is still a 5th-year maths
 * teacher, and a suggestion that flipped on their last experiment would be worse
 * than no suggestion at all. Ties go to whichever pair was seen first, and the
 * records arrive newest first — so the newest of the equally-common pairs wins
 * without the caller having to sort anything.
 *
 * Three things are left out, and each for the same reason the picker leaves them
 * out: a record with no level or subject names no pair to count, another room's
 * own capsule (`visibility: 'classroom'`) is never offered as material for a
 * module, and *nothing* is a legitimate answer — `null` means the studio shows
 * its chips unselected, which is the right first step for a creator who has not
 * filed anything yet.
 */
export function moduleFilingSuggestion(
  records: readonly ModuleCapsuleFacts[] | undefined,
): ModuleFiling | null {
  const counts = new Map<string, {filing: ModuleFiling; count: number}>()
  for (const record of records ?? []) {
    if (!record.level || !record.subject) continue
    if (record.visibility === 'classroom') continue
    const key = `${record.level}|${record.subject}`
    const existing = counts.get(key)
    if (existing) {
      existing.count += 1
    } else {
      counts.set(key, {
        filing: {level: record.level, subject: record.subject},
        count: 1,
      })
    }
  }

  let best: {filing: ModuleFiling; count: number} | null = null
  for (const entry of counts.values()) {
    if (!best || entry.count > best.count) best = entry
  }
  return best ? best.filing : null
}

/**
 * The coins a module is worth: every exercise in every capsule it holds.
 *
 * Read off `slideCount` rather than counted from the slides, because the
 * publisher already counted them — the number is the capsule's own answer to the
 * same question, and a second count here is a second answer waiting to drift.
 * The player's coin total, a card's "coins to win" and the test fixtures all read
 * this one function.
 */
export function moduleCoinCount(
  capsules: readonly ModuleCapsuleFacts[],
): number {
  return capsules.reduce(
    (total, capsule) =>
      total + (typeof capsule.slideCount === 'number' ? capsule.slideCount : 0),
    0,
  )
}

/** What one place in a playlist is, as the studio draws it. */
export type ModuleSlot =
  | {
      kind: 'filled'
      item: ModuleItem
      capsule: ModuleCapsuleFacts
      index: number
    }
  /** Written, not published: the module can hold it, but not go out with it. */
  | {
      kind: 'draft'
      item: ModuleItem
      capsule: ModuleCapsuleFacts
      index: number
    }
  /** Its record is gone: the slot has to be replaced. */
  | {kind: 'missing'; item: ModuleItem; index: number}
  /** It is no longer the module's subject or level, so it cannot stay. */
  | {
      kind: 'mismatch'
      item: ModuleItem
      capsule: ModuleCapsuleFacts
      index: number
    }

/**
 * The playlist, slot by slot, with each place's state decided once.
 *
 * The studio draws every slot from this rather than asking three questions of
 * each item in its render, and the reason is the same as everywhere else in this
 * file: *what state is this slot in* has one answer, and it is the same answer
 * the publish gate below is built on. A slot cannot look publishable and then be
 * refused.
 *
 * `mismatch` is possible even though the picker filters, and that is deliberate:
 * a record can be edited after it was chosen (a capsule moved to another year),
 * and a module that silently kept it would be a playlist whose card lied. Shown,
 * named and removable is the only honest handling.
 */
export function moduleSlots({
  items,
  capsules,
  level,
  subject,
}: {
  items: readonly ModuleItem[]
  capsules: readonly ModuleCapsuleFacts[]
  level?: string | null
  subject?: string | null
}): ModuleSlot[] {
  const byUri = new Map(capsules.map(capsule => [capsule.uri, capsule]))
  return items.map((item, index) => {
    const capsule = byUri.get(item.uri)
    if (!capsule) return {kind: 'missing', item, index}
    if (!isSameSubjectAndLevel(capsule, {level, subject})) {
      return {kind: 'mismatch', item, capsule, index}
    }
    const published = capsule.status !== 'draft' && !!capsule.postUri
    return published
      ? {kind: 'filled', item, capsule, index}
      : {kind: 'draft', item, capsule, index}
  })
}

/**
 * What is stopping this module from being published.
 *
 * Returned as *codes with data*, never as sentences: the module that says what is
 * wrong has to say it in the reader's language, and a lib that reached for the
 * i18n instance would make every test that checks this rule depend on a locale
 * file. The caller renders; this decides.
 *
 * The order is the order a creator fixes them in — the module's own identity
 * first, then the floor, then the individual capsules that are not ready — so the
 * list reads as a to-do rather than as a complaint.
 *
 * A module that is short of the floor reports **one** blocker, not one per
 * missing capsule: "add 2 more capsules" is a sentence somebody can act on, and
 * two identical lines under it are noise.
 */
export type ModuleBlocker =
  | {code: 'title'}
  | {code: 'subject'}
  | {code: 'level'}
  | {code: 'too-few'; missing: number; needed: number}
  | {code: 'duplicate'; uri: string}
  | {code: 'missing'; uri: string}
  | {code: 'unpublished'; uri: string; title: string}
  | {code: 'mismatch'; uri: string; title: string}

export function modulePublishBlockers({
  title,
  level,
  subject,
  items,
  capsules,
}: {
  title: string
  level: string | null | undefined
  subject: string | null | undefined
  items: readonly ModuleItem[]
  capsules: readonly ModuleCapsuleFacts[]
}): ModuleBlocker[] {
  const blockers: ModuleBlocker[] = []

  if (!title.trim()) blockers.push({code: 'title'})
  if (!subject) blockers.push({code: 'subject'})
  if (!level) blockers.push({code: 'level'})

  const seen = new Set<string>()
  const duplicated: string[] = []
  for (const item of items) {
    if (seen.has(item.uri)) duplicated.push(item.uri)
    seen.add(item.uri)
  }
  for (const uri of duplicated) blockers.push({code: 'duplicate', uri})

  const missing = Math.max(0, MODULE_MIN_CAPSULES - seen.size)
  if (missing > 0) {
    blockers.push({
      code: 'too-few',
      missing,
      needed: MODULE_MIN_CAPSULES,
    })
  }

  /*
   * Only slots that exist are checked for readiness.
   *
   * A playlist that is three capsules short should say *that*, once, rather than
   * also reporting the three capsules it does not have. The per-capsule blockers
   * below are about work the creator has actually placed.
   */
  for (const slot of moduleSlots({items, capsules, level, subject})) {
    if (slot.kind === 'missing') {
      blockers.push({code: 'missing', uri: slot.item.uri})
    } else if (slot.kind === 'mismatch') {
      blockers.push({
        code: 'mismatch',
        uri: slot.item.uri,
        title: slot.capsule.title ?? '',
      })
    } else if (slot.kind === 'draft') {
      blockers.push({
        code: 'unpublished',
        uri: slot.item.uri,
        title: slot.capsule.title ?? '',
      })
    }
  }

  return blockers
}

/** Whether the module as it stands can be published. */
export function canPublishModule(blockers: readonly ModuleBlocker[]): boolean {
  return blockers.length === 0
}

/**
 * How many of the module's places are ready, for the progress line.
 *
 * Counted from the slots rather than from the blocker list, because the two
 * answer different questions: the line says *how far along* the playlist is, and
 * the blockers say *what to do next*. A module with four filled places and two
 * empty ones reads "4 of 6 published" — which is the sentence the creator needs
 * before the list of jobs under it.
 */
export function moduleReadyCount(slots: readonly ModuleSlot[]): number {
  return slots.filter(slot => slot.kind === 'filled').length
}
